import { getSvgElement } from "../../../helpers/svg";
import iconAlert from "../assets/icon__alert.svg";
import iconSpinner from "../../../crew/components/file_uploader/assets/icon__spinner.svg";
import iconSadCloud from "../assets/icon__sad_cloud.svg";

/**
 * ImageLoader - Handles image loading, DOM management, and loading states
 * 
 * Responsibilities:
 * - Create and manage image wrapper and img elements
 * - Handle placeholder → full resolution image loading
 * - Manage loading/loaded/error states
 * - Emit events for state changes
 */
export class ImageLoader {
    constructor(slideElement, config = {}) {
        this.slideElement = slideElement;
        this.config = config;
        
        // Constants
        this.LOAD_TIMEOUT = 4000;
        
        // State
        this.isLoading = false;
        this.isLoaded = false;
        this.hasError = false;
        this.hasTimeout = false;
        this.hasPlaceholder = false;
        this.hasFullImage = false;
        this.imageNaturalWidth = 0;
        this.imageNaturalHeight = 0;
        
        // DOM elements
        this.imageWrapper = null;
        this.img = null;
        this.timeoutId = null;
        
        // Event listeners
        this.listeners = new Map();
        
        this.ensureImageStructure();
    }
    
    /**
     * Add event listener
     */
    addEventListener(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }
    
    /**
     * Remove event listener
     */
    removeEventListener(event, callback) {
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        }
    }
    
    /**
     * Emit event to listeners
     */
    emit(event, data = {}) {
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            callbacks.forEach(callback => callback(data));
        }
    }
    
    /**
     * Ensure the image wrapper and img elements exist
     */
    ensureImageStructure() {
        // Find or create wrapper
        this.imageWrapper = this.slideElement.querySelector('.carousel__image');
        if (!this.imageWrapper) {
            this.imageWrapper = document.createElement('div');
            this.imageWrapper.className = 'carousel__image';
            this.slideElement.appendChild(this.imageWrapper);
        }
        
        // Find or create spinner element (permanent in DOM, sibling of image wrapper)
        this.spinner = this.slideElement.querySelector('.carousel__slide__spinner');
        if (!this.spinner) {
            this.spinner = document.createElement('div');
            this.spinner.className = 'carousel__slide__spinner';
            const spinnerIcon = getSvgElement(iconSpinner);
            this.spinner.appendChild(spinnerIcon);
            this.slideElement.appendChild(this.spinner);
        }
        
        // Find or create error symbol element (permanent in DOM, sibling of image wrapper)
        this.errorSymbol = this.slideElement.querySelector('.carousel__slide__error');
        if (!this.errorSymbol) {
            this.errorSymbol = document.createElement('div');
            this.errorSymbol.className = 'carousel__slide__error';
            const errorIcon = getSvgElement(iconAlert);
            this.errorSymbol.appendChild(errorIcon);
            this.slideElement.appendChild(this.errorSymbol);
        }
        
        // Find or create timeout symbol element (permanent in DOM, sibling of image wrapper)
        this.timeoutSymbol = this.slideElement.querySelector('.carousel__slide__timeout');
        if (!this.timeoutSymbol) {
            this.timeoutSymbol = document.createElement('div');
            this.timeoutSymbol.className = 'carousel__slide__timeout';
            const timeoutIcon = getSvgElement(iconSadCloud);
            this.timeoutSymbol.appendChild(timeoutIcon);
            this.slideElement.appendChild(this.timeoutSymbol);
        }
        
        // Find or create placeholder img element
        this.placeholderImg = this.imageWrapper.querySelector('.carousel__image__img--placeholder');
        if (!this.placeholderImg) {
            this.placeholderImg = document.createElement('img');
            this.placeholderImg.className = 'carousel__image__img--placeholder';
            this.imageWrapper.appendChild(this.placeholderImg);
        }
        
        // Find or create full resolution img element  
        this.fullImg = this.imageWrapper.querySelector('.carousel__image__img');
        if (!this.fullImg) {
            this.fullImg = document.createElement('img');
            this.fullImg.className = 'carousel__image__img';
            this.imageWrapper.appendChild(this.fullImg);
        }
        
        // Keep this.img pointing to fullImg for backward compatibility
        this.img = this.fullImg;
        
        // Handle existing content - check both images
        if (this.img.src && this.img.complete && this.img.naturalWidth) {
            this.handleImageLoad(this.img);
        }
        if (this.placeholderImg.src && this.placeholderImg.complete && this.placeholderImg.naturalWidth) {
            this.handlePlaceholderLoad(this.placeholderImg);
        }
    }
    
    /**
     * Load image from slide config
     */
    loadFromConfig(slideConfig) {
        if (!slideConfig) return;
        
        const { url, placeholder, alt, caption, debug_timeout, debug_error } = slideConfig;
        
        this.setLoadingState(true);
        
        // Show placeholder or spinner immediately
        if (placeholder) {
            this.showPlaceholder(placeholder, alt);
        } else if (url) {
            this.showSpinner();
        }
        
        // Debug: Force timeout for testing
        if (debug_timeout) {
            console.log('[ImageLoader] Debug: Forcing timeout after 100ms');
            setTimeout(() => this.handleImageTimeout(), 100);
            return;
        }
        
        // Debug: Force error for testing  
        if (debug_error) {
            console.log('[ImageLoader] Debug: Forcing error after 100ms');
            setTimeout(() => this.handleImageError(), 100);
            return;
        }
        
        // Preload full resolution image
        if (url) {
            this.preloadFullImage(url, alt);
        } else {
            // No URL provided, just show placeholder and stop loading
            this.setLoadingState(false);
        }
        
        // Add caption if provided
        if (caption) {
            this.addCaption(caption);
        }
    }
    
    /**
     * Handle existing content (for slides with pre-existing images)
     */
    loadExistingContent() {
        if (this.img && this.img.src) {
            if (this.img.complete && this.img.naturalWidth) {
                // Image is already loaded
                this.handleImageLoad(this.img);
                this.setLoadingState(false);
                this.setLoadedState(true);
            } else {
                // Wait for image to load
                this.setLoadingState(true);
                this.img.onload = () => this.handleImageLoad(this.img);
                this.img.onerror = () => this.handleImageError();
            }
        }
    }
    
    /**
     * Show placeholder image
     */
    showPlaceholder(placeholderUrl, alt) {
        this.placeholderImg.src = placeholderUrl;
        if (alt) this.placeholderImg.alt = alt;
        
        // Calculate dimensions from placeholder
        this.placeholderImg.onload = () => this.handlePlaceholderLoad(this.placeholderImg);
        this.placeholderImg.onerror = () => this.handleImageError();
    }
    
    /**
     * Show loading spinner - no longer creates DOM elements, just shows existing spinner
     * The spinner visibility is now controlled by CSS classes on the slide element
     */
    showSpinner() {
        // The spinner is already in the DOM and will be shown via CSS when loading state is set
        // This method is now largely handled by the SlideRenderer via CSS classes
        console.log('[ImageLoader] showSpinner() called - spinner controlled by CSS classes');
    }
    
    /**
     * Preload full resolution image
     */
    preloadFullImage(url, alt) {
        const preloadImg = new Image();
        
        // Set up timeout
        this.timeoutId = setTimeout(() => {
            this.handleImageTimeout();
        }, this.LOAD_TIMEOUT);
        
        preloadImg.onload = () => {
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
                this.timeoutId = null;
            }
            this.swapToFullImage(url, alt, preloadImg.naturalWidth, preloadImg.naturalHeight);
            this.setLoadingState(false);
            this.setLoadedState(true);
        };
        
        preloadImg.onerror = () => {
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
                this.timeoutId = null;
            }
            this.setLoadingState(false);
            this.setErrorState(true);
        };
        
        preloadImg.src = url;
    }
    
    /**
     * Swap to full resolution image
     */
    swapToFullImage(url, alt, naturalWidth, naturalHeight) {
        // Clear timeout state since we're showing the full image
        if (this.hasTimeout) {
            this.setTimeoutState(false);
        }
        
        // Only update dimensions if we don't have them yet
        if (!this.imageNaturalWidth || !this.imageNaturalHeight) {
            this.imageNaturalWidth = naturalWidth;
            this.imageNaturalHeight = naturalHeight;
            this.emit('dimensionsKnown', {
                naturalWidth: this.imageNaturalWidth,
                naturalHeight: this.imageNaturalHeight
            });
        }
        
        // Set up event handler for when the actual DOM img finishes loading
        this.fullImg.onload = () => {
            // Now the full image is actually ready to display
            this.setFullImageLoaded(true);
            this.fullImg.onload = null; // Clean up
        };
        
        this.fullImg.onerror = () => {
            // Handle case where DOM img fails to load (even though preload succeeded)
            this.setErrorState(true);
            this.fullImg.onerror = null; // Clean up
        };
        
        // Set the src to start loading - this will trigger onload when ready
        this.fullImg.src = url;
        if (alt) this.fullImg.alt = alt;
        
        // Check if image loaded from cache instantly
        if (this.fullImg.complete && this.fullImg.naturalWidth) {
            this.setFullImageLoaded(true);
        }
    }
    
    /**
     * Handle image load event (for full image)
     */
    handleImageLoad(img) {
        this.imageNaturalWidth = img.naturalWidth;
        this.imageNaturalHeight = img.naturalHeight;
        
        // Clear timeout state since we have an image
        if (this.hasTimeout) {
            this.setTimeoutState(false);
        }
        
        this.emit('dimensionsKnown', {
            naturalWidth: this.imageNaturalWidth,
            naturalHeight: this.imageNaturalHeight
        });
    }
    
    /**
     * Handle placeholder image load event
     */
    handlePlaceholderLoad(img) {
        // Set dimensions if not already set
        if (!this.imageNaturalWidth || !this.imageNaturalHeight) {
            this.imageNaturalWidth = img.naturalWidth;
            this.imageNaturalHeight = img.naturalHeight;
            this.emit('dimensionsKnown', {
                naturalWidth: this.imageNaturalWidth,
                naturalHeight: this.imageNaturalHeight
            });
        }
        
        // Clear timeout state since we have a placeholder
        if (this.hasTimeout) {
            this.setTimeoutState(false);
        }
        
        // Set placeholder state
        this.setPlaceholderLoaded(true);
    }
    
    /**
     * Handle image error
     */
    handleImageError() {
        this.setLoadingState(false);
        this.setErrorState(true);
    }
    
    /**
     * Handle image timeout
     */
    handleImageTimeout() {
        this.timeoutId = null; // Clear reference
        
        // Only set timeout if no image has loaded (neither placeholder nor full)
        // and there's no error
        if (!this.isLoaded && !this.hasError && !this.hasPlaceholder) {
            this.setLoadingState(false);
            this.setTimeoutState(true);
        }
    }
    
    /**
     * Add caption to slide
     */
    addCaption(captionText) {
        // Check if caption already exists
        let captionElement = this.slideElement.querySelector('.carousel__slide__caption');
        
        if (!captionElement) {
            // Create new caption element
            captionElement = document.createElement('div');
            captionElement.className = 'carousel__slide__caption';
            
            const paragraph = document.createElement('p');
            paragraph.textContent = captionText;
            captionElement.appendChild(paragraph);
            
            this.slideElement.appendChild(captionElement);
        } else {
            // Update existing caption
            const paragraph = captionElement.querySelector('p');
            if (paragraph) {
                paragraph.textContent = captionText;
            }
        }
    }
    
    /**
     * Set loading state
     */
    setLoadingState(loading) {
        this.isLoading = loading;
        this.emit('loadingStateChanged', { isLoading: loading });
    }
    
    /**
     * Set loaded state
     */
    setLoadedState(loaded) {
        // Clear any pending timeout when marked as loaded
        if (loaded && this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
        
        // Clear timeout state if image loads after timeout
        if (loaded && this.hasTimeout) {
            this.setTimeoutState(false);
        }
        
        this.isLoaded = loaded;
        this.emit('loadedStateChanged', { isLoaded: loaded });
    }
    
    /**
     * Set error state
     */
    setErrorState(error) {
        // Clear any pending timeout when marked as error
        if (error && this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
        this.hasError = error;
        this.emit('errorStateChanged', { hasError: error });
    }
    
    /**
     * Set timeout state
     */
    setTimeoutState(timeout) {
        this.hasTimeout = timeout;
        this.emit('timeoutStateChanged', { hasTimeout: timeout });
    }
    
    /**
     * Set placeholder loaded state
     */
    setPlaceholderLoaded(loaded) {
        this.hasPlaceholder = loaded;
        this.emit('placeholderLoadedStateChanged', { hasPlaceholder: loaded });
    }
    
    /**
     * Set full image loaded state
     */
    setFullImageLoaded(loaded) {
        this.hasFullImage = loaded;
        this.emit('fullImageLoadedStateChanged', { hasFullImage: loaded });
    }
    
    /**
     * Get image wrapper element
     */
    getImageWrapper() {
        return this.imageWrapper;
    }
    
    /**
     * Get img element
     */
    getImg() {
        return this.img;
    }
    
    /**
     * Get image dimensions
     */
    getDimensions() {
        return {
            naturalWidth: this.imageNaturalWidth,
            naturalHeight: this.imageNaturalHeight
        };
    }
    
    /**
     * Get loading states
     */
    getStates() {
        return {
            isLoading: this.isLoading,
            isLoaded: this.isLoaded,
            hasError: this.hasError,
            hasTimeout: this.hasTimeout,
            hasPlaceholder: this.hasPlaceholder,
            hasFullImage: this.hasFullImage
        };
    }
    
    /**
     * Clean up resources
     */
    destroy() {
        // Clear any pending timeout
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
        
        this.listeners.clear();
        if (this.img) {
            this.img.onload = null;
            this.img.onerror = null;
        }
    }
}